// Wire-format types and API contract for Terms & Conditions Management (PRD 14).
// Mirrors the shape used by `diseaseCodeApi` so the adapter/error patterns stay
// consistent across features. See docs/api-contracts/term-management.md.

import type { PortalRole } from '@/app/store/auth.store';

// PRD 14 §4.3 / contract §2.1 — wire format is UPPERCASE English.
export type TermVersionStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

export const TERM_VERSION_STATUSES: readonly TermVersionStatus[] = [
  'DRAFT',
  'PUBLISHED',
  'ARCHIVED',
] as const;

export interface TermType {
  // v9 (DEC-DB-PK-BIGINT-001): ids are BIGINT UNSIGNED → number on the wire.
  id: number;
  name: string;
  // Present for the 4 default seeds only; FE i18n key suffix under
  // `terms:typeNames.<slug>`. Omitted for admin-added types — render `name` directly.
  slug?: string;
  // Whether consent is mandatory at sign-up (admin sign-up: US-SU-05; also patient
  // sign-up). Toggleable by Root. PRD 14 §4.4: orthogonal to `deletable`.
  required: boolean;
  // Empty string allowed. Max 200 chars.
  description: string;
  // System-controlled. false for the 4 default seeds; true for admin-added.
  // Not exposed on add/edit forms — set by BE on insert.
  deletable: boolean;
  // Server-computed. Count of versions of any status. FE disables the row
  // [Delete] button when > 0.
  versionCount: number;
}

// Snapshot of the admin who created the version. Subsequent renames / role
// changes on that admin do NOT mutate this snapshot.
export interface TermAuthor {
  name: string;
  email: string;
  // English role key, used to render the Role Badge in the author cell (PRD 14 §4.3).
  authorGrade: PortalRole;
}

export interface TermVersionSummary {
  id: number;
  typeId: number;
  // Must match `^v\d+\.\d+$` (e.g. "v1.0", "v1.5"). Unique within the owning type.
  version: string;
  status: TermVersionStatus;
  // Empty string allowed. Free text. BE may send null for "no note".
  revisionNote: string | null;
  // YYYY-MM-DD.
  effectiveDate: string;
  // ISO datetime. Server-assigned on create. Immutable.
  registeredAt: string;
  author: TermAuthor;
}

// Detail view adds the HTML body (heavy field — omitted from list responses).
export interface TermVersion extends TermVersionSummary {
  // HTML body authored via the contenteditable RTE. Stored verbatim.
  content: string;
}

export interface CreateTermVersionInput {
  version: string;
  revisionNote?: string;
  effectiveDate: string;
  content: string;
}

// `version` is immutable after creation (PRD 14 §3.4).
export interface UpdateTermVersionInput {
  revisionNote?: string;
  effectiveDate?: string;
  content?: string;
}

export interface CreateTermTypeInput {
  name: string;
  required: boolean;
  description?: string;
}

// `slug` and `deletable` are immutable on the wire.
export interface UpdateTermTypeInput {
  name?: string;
  required?: boolean;
  description?: string;
}

// Returned in `error.meta` when `VERSION_DUPLICATE` is raised so the form can
// surface which existing version is blocking the requested version number.
export interface VersionDuplicateMeta {
  existingVersionId: number;
  existingStatus: TermVersionStatus;
}

// Returned in `error.meta` when `TYPE_HAS_VERSIONS` is raised so the row
// [Delete] tooltip can render the count without re-fetching.
export interface TypeHasVersionsMeta {
  versionCount: number;
}

export type TermsApiErrorCode =
  | 'VERSION_REQUIRED'
  | 'VERSION_FORMAT_INVALID'
  | 'VERSION_DUPLICATE'
  | 'EFFECTIVE_DATE_REQUIRED'
  | 'EFFECTIVE_DATE_PAST'
  | 'CONTENT_REQUIRED'
  // SHCT-703 — [공통] 00 §3 / PRD 14 §4.2 rich-text cap (20,000 incl. HTML).
  | 'CONTENT_TOO_LONG'
  | 'TYPE_NAME_REQUIRED'
  | 'TYPE_NAME_TOO_LONG'
  | 'TYPE_NAME_DUPLICATE'
  | 'TYPE_DESCRIPTION_TOO_LONG'
  | 'TYPE_HAS_VERSIONS'
  | 'TYPE_NOT_DELETABLE'
  | 'VERSION_NOT_DELETABLE'
  | 'VERSION_NOT_PUBLISHABLE'
  | 'NOT_FOUND'
  | 'INSUFFICIENT_PERMISSION'
  // Transport failure (offline / DNS / timeout) — no HTTP response was received.
  | 'NETWORK'
  | 'UNKNOWN';

export class TermsApiError extends Error {
  readonly code: TermsApiErrorCode;
  readonly fields?: Record<string, string>;
  readonly meta?: Record<string, unknown>;

  constructor(
    code: TermsApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: Record<string, unknown> },
  ) {
    super(message);
    this.name = 'TermsApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, TermsApiError.prototype);
  }
}

export interface TermsApi {
  // PRD 14 §3.1 — full list of types, unpaginated. Ordered by id ASC
  // (4 default seeds first, then user-added in insertion order).
  listTypes(): Promise<TermType[]>;
  // PRD 14 §3.5 — Root only. `description` optional; empty/whitespace allowed.
  createType(input: CreateTermTypeInput): Promise<TermType>;
  // PRD 14 §3.5 — Root only. Default-seed `name`/`required`/`description` are
  // editable; `slug` and `deletable` are immutable (stripped if sent).
  updateType(id: number, input: UpdateTermTypeInput): Promise<TermType>;
  // PRD 14 §3.5 — Root only. Rejected with TYPE_NOT_DELETABLE on default seeds
  // and TYPE_HAS_VERSIONS when versionCount > 0.
  deleteType(id: number): Promise<void>;

  // PRD 14 §3.2 — full version list for a type, unpaginated. Ordered by
  // `version` DESC (latest first), lexicographic.
  listVersions(typeId: number): Promise<TermVersionSummary[]>;
  // PRD 14 §3.4 — detail view (includes the HTML body).
  getVersion(typeId: number, versionId: number): Promise<TermVersion>;
  // PRD 14 §3.3 — status is always DRAFT on create. Publishing is a separate
  // step (see publishVersion).
  createVersion(typeId: number, input: CreateTermVersionInput): Promise<TermVersion>;
  // PRD 14 §3.4 — DRAFT only. `version` is immutable.
  updateVersion(
    typeId: number,
    versionId: number,
    input: UpdateTermVersionInput,
  ): Promise<TermVersion>;
  // PRD 14 §3.3 — atomic transition: DRAFT → PUBLISHED, and the type's
  // previous PUBLISHED (if any) → ARCHIVED in the same server transaction.
  // BE-owned side-effect (auto notice + push); FE just toasts on success.
  publishVersion(typeId: number, versionId: number): Promise<void>;
  // PRD 14 §3.4 — Root only. DRAFT only (PUBLISHED/ARCHIVED rejected with
  // VERSION_NOT_DELETABLE per legal-preservation rule).
  deleteVersion(typeId: number, versionId: number): Promise<void>;
}
